/**
 * UserCardsAttachmentSection — własny załącznik budynku: PDF kart mieszkań operatora (przegląd zrobiony na papierze),
 * który bot wgrywa do protokołu CEEB ZAMIAST naszych wygenerowanych kart lokali (zadanie 4 etap 2, Bronek 2026-10-21;
 * migr. 079, backend routes/userCards.js).
 *
 * Za flagą serwera CEEB_OWN_CARDS_ATTACHMENT: wyłączona → GET zwraca `enabled: false` i sekcja NIE renderuje się wcale.
 * Lista z pobraniem; usunięcie tylko póki plik nie jest wgrany do CEEB (potwierdzenie ConfirmModal). Nowy plik zastępuje
 * poprzedni niewysłany. Błędy widoczne w sekcji (baner), bez okienek przeglądarki.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { userCardsApi, InspectionUpdateError, UserCardsAttachment, UserCardsList } from '../services/api';
import { saveBlob } from '../utils/download';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { ConfirmModal } from '../ds/ConfirmModal';

interface Props {
  inspectionId: string;
}

const boxStyle: React.CSSProperties = {
  marginTop: 'var(--space-4)',
  padding: 'var(--space-4)',
  background: 'var(--surface-fill)',
  borderRadius: 'var(--radius-md)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

const bannerStyle = (kind: 'error' | 'ok'): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  background: kind === 'error' ? 'var(--status-deny-bg)' : 'var(--status-ok-bg)',
  color: kind === 'error' ? 'var(--status-deny)' : 'var(--status-ok)',
  fontSize: 'var(--text-label)',
});

export function formatBytes(n: number | null): string {
  if (n == null) return '—';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} kB`;
  return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

const plDateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export function UserCardsAttachmentSection({ inspectionId }: Props) {
  const [list, setList] = useState<UserCardsList | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<UserCardsAttachment | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async (): Promise<UserCardsList | null> => {
    try {
      const l = await userCardsApi.list(inspectionId);
      setList(l);
      setLoadError(null);
      return l;
    } catch (e) {
      setLoadError((e as Error).message || 'nieznany błąd');
      return null;
    }
  }, [inspectionId]);

  /** Co pójdzie do CEEB po zmianie — wg ODŚWIEŻONEJ listy (starszy plik może już być w CEEB i dalej obowiązywać). */
  const nextInfo = (l: UserCardsList | null): string => {
    const next = l && l.activeId ? l.attachments.find((a) => a.id === l.activeId) : null;
    if (!next) return 'Bez własnego pliku do CEEB pójdą karty lokali wygenerowane przez system.';
    return next.ceebSyncedAt
      ? `Obowiązuje plik „${next.originalFilename || next.ceebFilename}”, już wgrany do CEEB — bot go nie wgra ponownie.`
      : `Do CEEB pójdzie plik „${next.originalFilename || next.ceebFilename}”.`;
  };

  useEffect(() => { load(); }, [load]);

  if (!list || !list.enabled) {
    // Flaga wyłączona (albo jeszcze nie wiemy) — sekcji nie ma. Nieudany odczyt listy (sieć / 500) — krótka informacja
    // w widoku (bez roli „alert": to opcjonalna funkcja, nie wynik akcji operatora; nie zagłusza błędów formularzy).
    return loadError ? (
      <div data-testid="user-cards-load-error" style={{ marginTop: 'var(--space-4)', fontSize: 'var(--text-caption)', color: 'var(--status-deny)' }}>
        Nie udało się sprawdzić własnego załącznika (karty mieszkań): {loadError}
      </div>
    ) : null;
  }

  const maxMb = Math.round(list.maxBytes / 1024 / 1024);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // ten sam plik można wybrać ponownie
    if (!file) return;
    setError(null);
    setNotice(null);
    if (!/\.pdf$/i.test(file.name)) { setError('Wybierz plik PDF (.pdf).'); return; }
    if (file.size > list.maxBytes) { setError(`Plik jest za duży (${formatBytes(file.size)}, limit ${maxMb} MB).`); return; }
    setBusy(true);
    try {
      const r = await userCardsApi.upload(inspectionId, file);
      const fresh = await load();
      setNotice(r.reused
        ? `Ten plik był już wgrany. ${nextInfo(fresh)}`
        : `Wgrano: ${r.attachment.originalFilename || file.name}. Pójdzie do CEEB przy najbliższym „Wyślij do CEEB”.`);
    } catch (err) {
      setError((err as Error).message || 'Nie udało się wgrać pliku.');
    } finally {
      setBusy(false);
    }
  };

  const download = async (a: UserCardsAttachment) => {
    setError(null);
    try {
      const f = await userCardsApi.download(inspectionId, a.id);
      saveBlob(f.blob, f.filename || a.ceebFilename || 'karty_mieszkan.pdf');
    } catch (err) {
      setError((err as Error).message || 'Nie udało się pobrać pliku.');
    }
  };

  const confirmDelete = async () => {
    const a = toDelete;
    setToDelete(null);
    if (!a) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await userCardsApi.remove(inspectionId, a.id);
      const fresh = await load();
      setNotice(`Plik usunięty. ${nextInfo(fresh)}`);
    } catch (err) {
      const e = err as InspectionUpdateError;
      setError(e.message || 'Nie udało się usunąć pliku.');
      if (e.status === 409) await load();
    } finally {
      setBusy(false);
    }
  };

  const active = list.attachments.filter((a) => !a.deletedAt);
  const hasSyncedActive = active.some((a) => a.ceebSyncedAt);

  return (
    <section aria-label="Własny załącznik — karty mieszkań" style={boxStyle} data-testid="user-cards-attachment">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 'var(--text-label)', fontWeight: 'var(--weight-semibold)', color: 'var(--text-primary)' }}>
          Własny załącznik do CEEB — karty mieszkań (PDF):
        </div>
        <Button variant="secondary" icon="upload" onClick={() => inputRef.current && inputRef.current.click()} disabled={busy}>
          {busy ? 'Trwa…' : active.length ? 'Wgraj nowy plik' : 'Wgraj plik PDF'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: 'none' }}
          data-testid="user-cards-input"
          onChange={onFile}
        />
      </div>

      {error && (
        <div role="alert" style={bannerStyle('error')}>
          <Icon name="triangle-alert" size={18} />
          <span>{error}</span>
        </div>
      )}
      {notice && !error && (
        <div role="status" style={bannerStyle('ok')}>
          <Icon name="check-circle" size={18} />
          <span>{notice}</span>
        </div>
      )}

      {list.attachments.length === 0 ? (
        <div style={{ fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
          Brak własnego pliku — do CEEB pójdą karty lokali wygenerowane przez system.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {list.attachments.map((a) => {
            const isNext = a.id === list.activeId;
            return (
              <li
                key={a.id}
                data-testid={`user-cards-row-${a.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', padding: 'var(--space-2) 0', borderTop: 'var(--border-width) solid var(--border)' }}
              >
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--text-body)', color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
                    {a.originalFilename || a.ceebFilename}
                  </div>
                  <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                    {formatBytes(a.sizeBytes)} · wgrany {plDateTime(a.createdAt)}{a.uploadedBy ? ` przez ${a.uploadedBy}` : ''} · w CEEB jako {a.ceebFilename}
                  </div>
                  <div style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--weight-semibold)', color: a.ceebSyncedAt ? 'var(--status-ok)' : 'var(--text-secondary)' }}>
                    {a.ceebSyncedAt
                      ? `W CEEB od ${plDateTime(a.ceebSyncedAt)}${a.deletedAt ? ' (usunięty w panelu — w CEEB zostaje)' : ''}`
                      : (isNext ? 'Czeka na wysyłkę do CEEB' : 'Niewysłany')}
                  </div>
                </div>
                <Button variant="ghost" icon="download" onClick={() => download(a)} disabled={busy}>Pobierz</Button>
                {!a.ceebSyncedAt && !a.deletedAt && (
                  <Button variant="ghost" icon="trash" onClick={() => setToDelete(a)} disabled={busy}>Usuń</Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
        Tylko PDF, do {maxMb} MB. Przy „Wyślij do CEEB” bot dołączy do protokołu ten plik ZAMIAST kart lokali generowanych
        przez system. Tekst o kartach mieszkań w „Inne uchybienia…” wpisze dopiero wtedy, gdy zobaczy plik na liście
        załączników w CEEB.
        {hasSyncedActive && ' Plik wgrany już do CEEB zostaje w protokole — bot nie usuwa plików w CEEB; nowy plik dołączy obok.'}
      </div>

      <ConfirmModal
        open={!!toDelete}
        title="Usunąć plik?"
        message={toDelete ? `Plik „${toDelete.originalFilename || toDelete.ceebFilename}” nie trafi do CEEB.${hasSyncedActive ? ' Obowiązywać będzie plik już wgrany do CEEB.' : ' Bez własnego pliku bot dołączy karty lokali wygenerowane przez system.'}` : ''}
        confirmLabel="Usuń plik"
        destructive
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </section>
  );
}
